'use client';

import { useState, useEffect } from 'react';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import NewCustomDataTable from '../../common/NewCustomDataTable.jsx';
import { getMe } from '../../../../services/AuthService';
import { getRoles } from '../../../../services/RolesServices.js';
import {
  getStudentProfileById,
  updateStudentProfile,
  getStudentAttendancePercentage,
} from '../../../../services/StudentService';
import { getBatches } from '../../../../services/BatchService';
import { getReceipts, getReceiptById } from '../../../../services/StudentReceiptService.js';
import { useAuth } from '../../../../context/AuthContext';
import { toast } from '../../common/Toast/toast';
import {
  AlertCircle,
  Award,
  BookOpen,
  Calendar,
  CheckCircle,
  CreditCard,
  Download,
  Edit,
  Eye,
  File,
  FileText,
  GraduationCap,
  Layers,
  Save,
  Settings,
  Shield,
  TrendingUp,
  Upload,
  User,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import '../../Trainer/TrainerDashboard.css';
import '../../Dashboard/BusinessPulse.css';
import './UserProfile.css';

/* The half circle the two dashboards and /student-profile all draw, at
   the same radius, so the four cannot drift apart. */
const GAUGE_R = 80;
const GAUGE_ARC = `M 20 100 A ${GAUGE_R} ${GAUGE_R} 0 0 1 180 100`;
const GAUGE_LEN = Math.PI * GAUGE_R;

const DASH = '—';

/* A value the record does not carry is a dash, never a zero. A zero is
   a reading; a dash says nothing was recorded. */
const textOf = (value) => {
  if (value === null || value === undefined) return DASH;
  const s = String(value).trim();
  return s === '' ? DASH : s;
};

const moneyOf = (value) => {
  if (value === null || value === undefined || value === '') return DASH;
  const n = Number(value);
  if (!Number.isFinite(n)) return DASH;
  return `₹${n.toLocaleString('en-IN')}`;
};

const dateOf = (value) => {
  if (!value) return DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? DASH
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const initialsOf = (name) =>
  String(name || '')
    .split(' ')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || 'U';

const UserProfile = () => {
  const { user: authUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [primaryRole, setPrimaryRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('Account Details');

  // Student-specific states
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [batchesData, setBatchesData] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [receiptsData, setReceiptsData] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [motivationalQuote, setMotivationalQuote] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeFileUrl, setResumeFileUrl] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [incompleteFields, setIncompleteFields] = useState([]);

  // Motivational quotes for students
  const motivationalQuotes = [
    'Success is the sum of small efforts repeated day in and day out.',
    'The expert in anything was once a beginner.',
    "Don't watch the clock; do what it does. Keep going.",
    'The secret of getting ahead is getting started.',
    "You don't have to be great to start, but you have to start to be great.",
    "Believe you can and you're halfway there.",
    'Your education is a dress rehearsal for a life that is yours to lead.',
    'Learning is a treasure that will follow its owner everywhere.',
  ];

  const getRandomQuote = () => {
    const now = new Date();
    const storedQuote = localStorage.getItem('motivationalQuote');
    const storedDate = localStorage.getItem('motivationalQuoteDate');

    if (storedQuote && storedDate) {
      const quoteDate = new Date(storedDate);
      const diffDays = Math.floor((now - quoteDate) / (1000 * 60 * 60 * 24));
      if (diffDays < 3) {
        return storedQuote;
      }
    }

    const randomIndex = Math.floor(Math.random() * motivationalQuotes.length);
    const newQuote = motivationalQuotes[randomIndex];
    localStorage.setItem('motivationalQuote', newQuote);
    localStorage.setItem('motivationalQuoteDate', now.toString());
    return newQuote;
  };

  // Helper function to determine if user has a specific role
  const hasRole = (roleValue) => {
    return userRoles.some((role) => role.value === roleValue || role.value === `lms-${roleValue}`);
  };

  const isStudent = () => hasRole('student') || hasRole('lms-student');
  const isInstructor = () => hasRole('instructor') || hasRole('trainer');
  const isAdmin = () => hasRole('admin') || hasRole('superadmin') || hasRole('manager');

  // Map role values to display names and types
  const getRoleDisplayInfo = (roleValue) => {
    const roleMap = {
      student: { display: 'Student', type: 'student' },
      'lms-student': { display: 'Student', type: 'student' },
      instructor: { display: 'Instructor', type: 'instructor' },
      trainer: { display: 'Trainer', type: 'instructor' },
      manager: { display: 'Manager', type: 'admin' },
      counsellor: { display: 'Counsellor', type: 'admin' },
      accountant: { display: 'Accountant', type: 'admin' },
      superadmin: { display: 'Super Admin', type: 'superadmin' },
      admin: { display: 'Admin', type: 'admin' },
      user: { display: 'User', type: 'user' },
    };
    return roleMap[roleValue] || { display: roleValue, type: 'user' };
  };

  useEffect(() => {
    if (hasRole('student') || hasRole('lms-student')) {
      setMotivationalQuote(getRandomQuote());
    }
  }, [userRoles]);

  // Validation schemas based on role
  const getValidationSchema = (roleType) => {
    const baseSchema = {
      firstName: Yup.string().required('First Name is required'),
      lastName: Yup.string().required('Last Name is required'),
      email: Yup.string().email('Invalid email').required('Email is required'),
    };

    if (roleType === 'student') {
      return Yup.object({
        ...baseSchema,
        phone: Yup.string().required('Phone number is required'),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        documentNo: Yup.string().required('Document Number is required'),
        collegeName: Yup.string().required('College Name is required'),
        courseName: Yup.string().required('Course Name is required'),
        permanentAddress: Yup.string().required('Permanent Address is required'),
        currentAddress: Yup.string().required('Current Address is required'),
        emergencyContactName: Yup.string().required('Emergency Contact Name is required'),
        emergencyContactRelationship: Yup.string().required(
          'Emergency Contact Relationship is required'
        ),
        emergencyContactPhone: Yup.string().required('Emergency Contact Phone is required'),
      });
    }

    if (roleType === 'instructor') {
      return Yup.object({
        ...baseSchema,
        specialization: Yup.string().required('Specialization is required'),
        experience: Yup.string().required('Experience is required'),
        qualification: Yup.string().required('Qualification is required'),
      });
    }

    return Yup.object(baseSchema);
  };

  // Get tabs based on user roles
  const getTabs = (roleType) => {
    const baseTabs = ['Account Details'];

    if (roleType === 'student') {
      return [...baseTabs, 'Batch Information', 'Billing Information', 'Resume'];
    }
    if (roleType === 'instructor') {
      return [...baseTabs, 'Teaching Information', 'Schedule'];
    }
    if (roleType === 'superadmin') {
      return [...baseTabs, 'System Settings', 'Reports'];
    }
    return baseTabs;
  };

  /* The tab strip carries a count only where there is something real to
     count. A tab that holds a placeholder gets no number. */
  const tabCount = (tab) => {
    if (tab === 'Batch Information') return batchesData.length;
    if (tab === 'Billing Information') return receiptsData.length;
    return undefined;
  };

  // Get role icon
  const getRoleIcon = (roleType) => {
    switch (roleType) {
      case 'student':
        return GraduationCap;
      case 'instructor':
        return UserCheck;
      case 'superadmin':
        return Shield;
      default:
        return User;
    }
  };

  // Function to determine payment status based on remaining fees
  const getPaymentStatus = (receipt, studentData) => {
    // Check if remaining fees is 0 or if receipt has explicit paid status
    const remainingFees = studentData?.remainingFees || 0;
    const isPaidByRemainingFees = remainingFees === 0;
    const isPaidByStatus = receipt?.status?.toLowerCase() === 'paid';

    return isPaidByRemainingFees || isPaidByStatus ? 'Paid' : 'Pending';
  };

  /* One word for the figure, and the tone the figure is drawn in. Both
     are only meaningful once a percentage exists — an unmeasured
     attendance is not "needs improvement", it is unmeasured. */
  const getAttendanceStatus = (percentage) => {
    if (percentage === null) return { text: 'Not recorded', tone: '' };
    if (percentage >= 80) return { text: 'Excellent', tone: 'up-paid' };
    if (percentage >= 60) return { text: 'Good', tone: '' };
    return { text: 'Needs improvement', tone: 'up-due' };
  };

  // Handle resume file upload
  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setResumeFile(file);
      const fileUrl = URL.createObjectURL(file);
      setResumeFileUrl(fileUrl);
    } else {
      setResumeFile(null);
      setResumeFileUrl(null);
      toast.error('Please upload a PDF file only.');
    }
  };

  /* An account states its roles in one of two shapes. A student comes
     back with `roles`, a list of ids to look up against the roles
     endpoint. Everyone else comes back with `profiles`, which already
     carry their own labels and are never ids into that list.

     Reading only the first shape left every staff account with no roles
     at all -- which is why the page read "Roles 0" over a profile that
     plainly had one, and why the heading fell back to "User profile". */
  const rolesFromProfiles = (account) => {
    const list = Array.isArray(account?.profiles) ? account.profiles : [];
    const mapped = list
      .map((profile, i) => {
        const label = String(profile?.label || profile?.name || profile?.value || '').trim();
        if (!label) return null;
        return {
          id: profile?.id || `profile-${i}`,
          label,
          value: String(profile?.value || label)
            .toLowerCase()
            .replace(/\s+/g, ''),
        };
      })
      .filter(Boolean);

    if (mapped.length > 0) return mapped;

    /* A super admin may carry the flag and nothing else. */
    const flagged =
      account?.isSuperAdmin === true ||
      account?.isSuperAdmin === 'true' ||
      account?.isSuperAdmin === 1 ||
      String(account?.profileIds || '').toLowerCase() === 'admin';

    return flagged ? [{ id: 'superadmin', label: 'Super Admin', value: 'superadmin' }] : [];
  };

  /* Which of an account's roles drives the tab set and the heading. */
  const applyRoles = (mappedRoles) => {
    setUserRoles(mappedRoles);

    if (mappedRoles.length > 0) {
      const rolePriority = [
        'student',
        'lms-student',
        'instructor',
        'trainer',
        'manager',
        'counsellor',
        'accountant',
        'superadmin',
        'admin',
        'user',
      ];

      let primaryRoleValue = mappedRoles[0].value; // Default to first role

      // Find the highest priority role
      for (const priority of rolePriority) {
        const foundRole = mappedRoles.find((role) => role.value === priority);
        if (foundRole) {
          primaryRoleValue = foundRole.value;
          break;
        }
      }

      setPrimaryRole(getRoleDisplayInfo(primaryRoleValue));
    } else {
      setPrimaryRole({ display: 'User', type: 'user' });
    }

    return mappedRoles;
  };

  // Fetch and map user roles
  const fetchAndMapRoles = async (userRoleIds, account) => {
    try {
      // Fetch all available roles
      const rolesResponse = await getRoles();

      // Map user role IDs to role objects
      const mappedRoles = [];

      if (Array.isArray(userRoleIds)) {
        userRoleIds.forEach((roleId) => {
          // First try to find by ID
          let roleObj = rolesResponse.data.find((role) => role.id === roleId);

          // If not found by ID, try to find by value (in case roleId is actually a role value)
          if (!roleObj) {
            roleObj = rolesResponse.data.find((role) => role.value === roleId);
          }

          if (roleObj) {
            mappedRoles.push(roleObj);
          } else {
            console.warn(` Role ${roleId} not found in available roles`);
          }
        });
      }

      return applyRoles(mappedRoles);
    } catch (err) {
      console.error(' Error fetching and mapping roles:', err);
      // Fallback to whatever the account states about itself.
      return applyRoles(rolesFromProfiles(account));
    }
  };

  // Function to fetch student receipts using getReceipts and filter by student data
  const fetchStudentReceipts = async (studentData) => {
    try {
      setReceiptsLoading(true);

      const response = await getReceipts(50000000, studentData.email);

      const receipts = Array.isArray(response?.data)
        ? response.data
        : response?.data
        ? [response.data]
        : [];
      const updatedReceipts = receipts.map((receipt) => ({
        ...receipt,
        status: getPaymentStatus(receipt, studentData),
      }));
      setReceiptsData(updatedReceipts);
      setReceiptsLoading(false);
    } catch (err) {
      setReceiptsData([]);
      setReceiptsLoading(false);
    }
  };

  // Fetch all batches and filter by student email
  const fetchBatchesDataByEmail = async (studentEmail) => {
    try {
      setBatchLoading(true);
      // Fetch all batches (large limit, adjust as needed)
      const response = await getBatches(50000000);
      if (response && response.data) {
        // Each batch should have a students array with email property
        const allBatches = Array.isArray(response.data) ? response.data : [response.data];
        const matchedBatches = allBatches.filter((batch) => {
          if (!batch.students || !Array.isArray(batch.students)) return false;
          return batch.students.some(
            (student) => student.email && student.email.toLowerCase() === studentEmail.toLowerCase()
          );
        });
        setBatchesData(matchedBatches);
      } else {
        setBatchesData([]);
      }
      setBatchLoading(false);
    } catch (err) {
      setBatchesData([]);
      setBatchLoading(false);
    }
  };

  // Fetch student-specific data - always fetch receipts and batches for students
  const fetchStudentData = async (userId) => {
    try {
      setBatchLoading(true);
      setReceiptsLoading(true);

      // Fetch student profile to get batch information
      try {
        const studentResponse = await getStudentProfileById(userId);
        const studentData = studentResponse.data;

        // Fetch student receipts using the student data (by email)
        await fetchStudentReceipts(studentData);
        // Fetch batches by email
        if (studentData.email) {
          await fetchBatchesDataByEmail(studentData.email);
        } else {
          setBatchesData([]);
        }
        // Fetch attendance stats if batchId exists
        const batchIds = studentData.batchIds || studentData.batchId;
        const firstBatchId = Array.isArray(batchIds) ? batchIds[0] : batchIds;
        if (firstBatchId) {
          try {
            const attendanceResponse = await getStudentAttendancePercentage(userId, firstBatchId);
            setAttendanceStats(attendanceResponse.data);
          } catch (attendanceError) {
            console.error('Error fetching attendance:', attendanceError);
          }
        }
      } catch (err) {
        console.error('Error fetching student profile:', err);
        setBatchesData([]);
      }
      setBatchLoading(false);
      setReceiptsLoading(false);
    } catch (err) {
      console.error('Error fetching student data:', err);
      setBatchLoading(false);
      setReceiptsLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        let fetchedUserData = null;

        // First check if we have user data from AuthContext
        if (authUser) {
          fetchedUserData = authUser;
        } else {
          // If not, fetch fresh data from API
          const response = await getMe();
          fetchedUserData = response.data.user || response.data;
        }

        setUserData(fetchedUserData);

        // Fetch and map roles
        {
          const roleIds = Array.isArray(fetchedUserData.roles) ? fetchedUserData.roles : [];
          const mappedRoles =
            roleIds.length > 0
              ? await fetchAndMapRoles(roleIds, fetchedUserData)
              : applyRoles(rolesFromProfiles(fetchedUserData));

          // Check if user has student role and fetch student-specific data
          const hasStudentRole = mappedRoles.some(
            (role) => role.value === 'student' || role.value === 'lms-student'
          );

          if (hasStudentRole) {
            await fetchStudentData(fetchedUserData.id || fetchedUserData._id);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error(' Failed to fetch user data:', err);
        setError(err.message || 'Failed to load user profile');
        setLoading(false);
      }
    };

    fetchUserData();
  }, [authUser]);

  const handleSubmit = async (values) => {
    try {
      if (hasRole('student') || hasRole('lms-student')) {
        await updateStudentProfile(userData.id || userData._id, values);
      }

      setUserData((prev) => ({ ...prev, ...values }));
      setEditMode(false);
      toast.success('Profile updated successfully');
    } catch (err) {
      console.error(' Error updating profile:', err);
      toast.error('Failed to update profile');
    }
  };

  const handleViewReceipt = async (receiptData) => {
    try {
      if (receiptData.id || receiptData._id) {
        const receiptId = receiptData.id || receiptData._id;
        const response = await getReceiptById(receiptId);
        setSelectedReceipt(response.data || receiptData);
      } else {
        setSelectedReceipt(receiptData);
      }
      setShowReceiptModal(true);
    } catch (err) {
      console.error(' Error fetching receipt details:', err);
      setSelectedReceipt(receiptData);
      setShowReceiptModal(true);
    }
  };

  // Profile completeness check every 15 minutes for students
  useEffect(() => {
    if (!userData || primaryRole?.type !== 'student') return;
    const validationSchema = getValidationSchema('student');
    const checkCompleteness = () => {
      try {
        validationSchema.validateSync(userData, { abortEarly: false });
        setShowIncompleteModal(false);
        setIncompleteFields([]);
      } catch (err) {
        if (err && err.inner) {
          setIncompleteFields(err.inner.map((e) => e.message));
        } else if (err && err.message) {
          setIncompleteFields([err.message]);
        } else {
          setIncompleteFields(['Profile is incomplete.']);
        }
        setShowIncompleteModal(true);
      }
    };
    checkCompleteness();
    const interval = setInterval(checkCompleteness, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userData, primaryRole]);

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page pg">
          <div className="pg-state">
            <span className="pg-spinner" aria-hidden="true" />
            <h3 className="pg-state-title">Loading profile</h3>
            <p className="pg-state-text">Fetching your account details.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page pg">
          <div className="pg-state">
            <span className="pg-state-icon">
              <AlertCircle size={26} aria-hidden="true" />
            </span>
            <h3 className="pg-state-title">Unable to load profile</h3>
            <p className="pg-state-text">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!userData || !primaryRole) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page pg">
          <div className="pg-state">
            <span className="pg-state-icon">
              <User size={26} aria-hidden="true" />
            </span>
            <h3 className="pg-state-title">No profile found</h3>
            <p className="pg-state-text">There is no account record to show.</p>
          </div>
        </div>
      </div>
    );
  }

  const RoleIcon = getRoleIcon(primaryRole.type);
  const tabs = getTabs(primaryRole.type);
  const validationSchema = getValidationSchema(primaryRole.type);

  const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();

  /* No default percentage. A missing figure is a missing figure — the
     gauge is simply not drawn, rather than drawn at an invented 40. */
  const rawAttendance = attendanceStats?.attendancePercentage;
  const attendancePercentage =
    rawAttendance === null || rawAttendance === undefined || rawAttendance === ''
      ? null
      : Number(rawAttendance);
  const hasAttendance = attendancePercentage !== null && Number.isFinite(attendancePercentage);
  const attendanceStatus = getAttendanceStatus(hasAttendance ? attendancePercentage : null);

  /* ── The readouts beside the hero ───────────────────────────────
     Role decides which figures exist at all; a tile is only built for
     something the record actually carries. */
  const tiles = isStudent()
    ? [
        { icon: BookOpen, label: 'Course', value: textOf(userData.courseName) },
        {
          icon: TrendingUp,
          label: 'Attendance',
          value: hasAttendance ? `${attendancePercentage}%` : DASH,
          tone: hasAttendance ? attendanceStatus.tone : 'up-none',
        },
        {
          icon: CheckCircle,
          label: 'Classes attended',
          value: textOf(attendanceStats?.presentCount),
        },
        { icon: Calendar, label: 'Classes held', value: textOf(attendanceStats?.totalLectures) },
        { icon: Layers, label: 'Batches', value: batchesData.length },
        {
          icon: CreditCard,
          label: 'Fees due',
          value: moneyOf(userData.remainingFees),
          tone: Number(userData.remainingFees) > 0 ? 'up-due' : '',
        },
      ]
    : /* A tile is for a figure. An account with no figures behind it --
         no attendance, no batches, no fees -- gets none, rather than
         three tiles restating the name, role and address written a few
         centimetres above them in the hero. */
      [];

  /* ── Read-only account fields ───────────────────────────────────
     What is shown depends on the role, and every one of them renders
     as a dash when the record has nothing. */
  const personalFields = [
    { label: 'First name', value: userData.firstName },
    { label: 'Last name', value: userData.lastName },
    { label: 'Email', value: userData.email, wide: true },
    ...(isStudent()
      ? [
          { label: 'Phone', value: userData.phone },
          { label: 'City', value: userData.city },
          { label: 'State', value: userData.state },
          { label: 'Document number', value: userData.documentNo },
          { label: 'College', value: userData.collegeName },
          { label: 'Course', value: userData.courseName },
          { label: 'Permanent address', value: userData.permanentAddress, wide: true },
          { label: 'Current address', value: userData.currentAddress, wide: true },
        ]
      : []),
  ];

  const emergencyFields = [
    { label: 'Contact name', value: userData.emergencyContactName },
    { label: 'Relationship', value: userData.emergencyContactRelationship },
    { label: 'Contact phone', value: userData.emergencyContactPhone },
  ];

  const professionalFields = [
    { label: 'Specialization', value: userData.specialization },
    { label: 'Experience', value: userData.experience },
    { label: 'Qualification', value: userData.qualification, wide: true },
  ];

  const renderFields = (fields) => (
    <dl className="up-fields">
      {fields.map(({ label, value, wide }) => {
        const shown = textOf(value);
        return (
          <div key={label} className={wide ? 'up-field--wide' : undefined}>
            <dt>{label}</dt>
            <dd className={shown === DASH ? 'is-empty' : undefined}>{shown}</dd>
          </div>
        );
      })}
    </dl>
  );

  const renderInput = (label, name, type, wide) => (
    <div className={`pg-field${wide ? ' up-field--wide' : ''}`} key={name}>
      <label className="pg-label" htmlFor={`up-${name}`}>
        {label}
      </label>
      <Field className="pg-input" id={`up-${name}`} name={name} type={type || 'text'} />
      <ErrorMessage name={name} component="div" className="up-err" />
    </div>
  );

  /* ── Batch list ─────────────────────────────────────────────────
     Five columns and no more; the rest of the record opens in the
     popup a row leads to, the same way the attendance list works. */
  const batchHeaders = [
    {
      title: 'Batch',
      prop: 'name',
      searchable: true,
      sortable: true,
      cell: (row) => <span className="pg-strong">{textOf(row.name || row.batchName)}</span>,
    },
    {
      title: 'Course',
      prop: 'course',
      sortable: true,
      cell: (row) => <span>{textOf(row.courseId?.[0]?.name || row.course)}</span>,
    },
    {
      title: 'Trainer',
      prop: 'instructor',
      sortable: true,
      cell: (row) => <span>{textOf(row.trainerId?.[0]?.trainerId || row.instructor)}</span>,
    },
    {
      title: 'Starts',
      prop: 'startDate',
      sortable: true,
      cell: (row) => <span className="pg-date">{dateOf(row.startDate)}</span>,
    },
    {
      title: 'Status',
      prop: 'status',
      sortable: true,
      cell: (row) =>
        row.status ? (
          <span className={`pg-pill${row.status === 'Active' ? ' pg-pill--live' : ''}`}>
            {row.status}
          </span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
  ];

  /* ── Receipt list ───────────────────────────────────────────────── */
  const receiptHeaders = [
    {
      title: 'Receipt',
      prop: 'receiptNumber',
      searchable: true,
      sortable: true,
      cell: (row) => (
        <span className="pg-strong">
          {row.receiptNumber || row._id?.slice(-6) || row.id?.slice(-6) || DASH}
        </span>
      ),
    },
    {
      title: 'Date',
      prop: 'createdAt',
      sortable: true,
      cell: (row) => <span className="pg-date">{dateOf(row.createdAt || row.date)}</span>,
    },
    {
      title: 'Amount',
      prop: 'amount',
      sortable: true,
      cell: (row) => <span>{moneyOf(row.amount ?? row.totalAmount)}</span>,
    },
    {
      title: 'Mode',
      prop: 'paymentMode',
      sortable: true,
      cell: (row) => <span>{textOf(row.paymentMode)}</span>,
    },
    {
      title: 'Status',
      prop: 'status',
      sortable: true,
      cell: (row) =>
        row.status ? (
          <span className={`pg-pill${row.status === 'Paid' ? ' pg-pill--good' : ' pg-pill--warn'}`}>
            {row.status}
          </span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
  ];

  const rowKeyProps = (label, open) => ({
    tabIndex: 0,
    role: 'button',
    'aria-label': label,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    },
  });

  /* ── The batch popup ───────────────────────────────────────────── */
  const batchDetail = selectedBatch
    ? (() => {
        const b = selectedBatch;
        const facts = [
          { label: 'Course', value: textOf(b.courseId?.[0]?.name || b.course) },
          { label: 'Trainer', value: textOf(b.trainerId?.[0]?.trainerId || b.instructor) },
          { label: 'Starts', value: dateOf(b.startDate) },
          { label: 'Ends', value: dateOf(b.endDate) },
          { label: 'Status', value: textOf(b.status) },
        ];

        return (
          <div
            className="pg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="up-batch-title"
            onClick={() => setSelectedBatch(null)}
          >
            <div
              className="pg-modal-panel pg-modal-panel--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="pgm-head">
                <div className="pgm-head-main">
                  <h2 className="pgm-batch" id="up-batch-title">
                    {textOf(b.name || b.batchName)}
                  </h2>
                  <dl className="pgm-ident">
                    <div className="pgm-ident-row">
                      <dt>Course</dt>
                      <dd>{textOf(b.courseId?.[0]?.name || b.course)}</dd>
                    </div>
                    <div className="pgm-ident-row">
                      <dt>Status</dt>
                      <dd>{textOf(b.status)}</dd>
                    </div>
                    <div className="pgm-ident-row">
                      <dt>Starts</dt>
                      <dd>{dateOf(b.startDate)}</dd>
                    </div>
                  </dl>
                </div>

                <button
                  type="button"
                  className="pg-modal-close"
                  onClick={() => setSelectedBatch(null)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </header>

              <div className="pg-modal-body">
                <section className="pgm-section">
                  <h3 className="pgm-section-title">Enrolment</h3>
                  <table className="pgm-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Detail</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facts.map((f, i) => (
                        <tr key={f.label}>
                          <td className="pgm-num">{i + 1}</td>
                          <td>{f.label}</td>
                          <td>{f.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>

                <section className="pgm-section">
                  <h3 className="pgm-section-title">Description</h3>
                  {b.description ? (
                    <p className="pgm-sub">{b.description}</p>
                  ) : (
                    <p className="pgm-empty">This batch carries no description.</p>
                  )}
                </section>
              </div>

              <footer className="pg-modal-foot">
                <div className="pgm-actions" />
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setSelectedBatch(null)}
                >
                  Close
                </button>
              </footer>
            </div>
          </div>
        );
      })()
    : null;

  /* ── The receipt popup ─────────────────────────────────────────── */
  const receiptDetail =
    showReceiptModal && selectedReceipt
      ? (() => {
          const r = selectedReceipt;
          const payment = [
            { label: 'Amount', value: moneyOf(r.amount ?? r.totalAmount) },
            { label: 'Payment method', value: textOf(r.paymentMode) },
            { label: 'Date', value: dateOf(r.createdAt || r.date) },
            { label: 'Status', value: textOf(r.status) },
            { label: 'Due date', value: dateOf(r.feesDueDate) },
          ];
          /* No database id here either -- the name and the address say who
             the receipt belongs to; a 24-character hex string does not. */
          const student = [
            { label: 'Name', value: textOf(fullName) },
            { label: 'Email', value: textOf(userData.email) },
            { label: 'Course', value: textOf(userData.courseName) },
          ];
          const items = Array.isArray(r.items) ? r.items : [];

          return (
            <div
              className="pg-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="up-receipt-title"
              onClick={() => setShowReceiptModal(false)}
            >
              <div
                className="pg-modal-panel pg-modal-panel--wide"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="pgm-head">
                  <div className="pgm-head-main">
                    <h2 className="pgm-batch" id="up-receipt-title">
                      Receipt {r.receiptNumber || r._id?.slice(-6) || DASH}
                    </h2>
                    <dl className="pgm-ident">
                      <div className="pgm-ident-row">
                        <dt>Amount</dt>
                        <dd>{moneyOf(r.amount ?? r.totalAmount)}</dd>
                      </div>
                      <div className="pgm-ident-row">
                        <dt>Status</dt>
                        <dd>{textOf(r.status)}</dd>
                      </div>
                      <div className="pgm-ident-row">
                        <dt>Date</dt>
                        <dd>{dateOf(r.createdAt || r.date)}</dd>
                      </div>
                    </dl>
                  </div>

                  <button
                    type="button"
                    className="pg-modal-close"
                    onClick={() => setShowReceiptModal(false)}
                    aria-label="Close"
                  >
                    &times;
                  </button>
                </header>

                <div className="pg-modal-body">
                  <section className="pgm-section">
                    <h3 className="pgm-section-title">Payment</h3>
                    <table className="pgm-table">
                      <thead>
                        <tr>
                          <th className="pgm-num">#</th>
                          <th>Detail</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payment.map((f, i) => (
                          <tr key={f.label}>
                            <td className="pgm-num">{i + 1}</td>
                            <td>{f.label}</td>
                            <td>{f.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </section>

                  <section className="pgm-section">
                    <h3 className="pgm-section-title">Student</h3>
                    <table className="pgm-table">
                      <thead>
                        <tr>
                          <th className="pgm-num">#</th>
                          <th>Detail</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {student.map((f, i) => (
                          <tr key={f.label}>
                            <td className="pgm-num">{i + 1}</td>
                            <td>{f.label}</td>
                            <td>{f.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </section>

                  <section className="pgm-section">
                    <h3 className="pgm-section-title">
                      Items
                      <span className="pgm-section-count">{items.length}</span>
                    </h3>
                    {items.length === 0 ? (
                      <p className="pgm-empty">This receipt lists no separate items.</p>
                    ) : (
                      <table className="pgm-table">
                        <thead>
                          <tr>
                            <th className="pgm-num">#</th>
                            <th>Item</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, i) => (
                            <tr key={`${item.name || 'item'}-${i}`}>
                              <td className="pgm-num">{i + 1}</td>
                              <td>{textOf(item.name)}</td>
                              <td>{item.quantity || 1}</td>
                              <td>{moneyOf(item.price)}</td>
                              <td>{moneyOf((item.quantity || 1) * (item.price || 0))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </section>

                  {r.description && (
                    <section className="pgm-section">
                      <h3 className="pgm-section-title">Description</h3>
                      <p className="pgm-sub">{r.description}</p>
                    </section>
                  )}
                </div>

                <footer className="pg-modal-foot">
                  <div className="pgm-actions" />
                  <button
                    type="button"
                    className="pg-btn pg-btn--main"
                    onClick={() => setShowReceiptModal(false)}
                  >
                    Close
                  </button>
                </footer>
              </div>
            </div>
          );
        })()
      : null;

  /* ── The resume preview popup ──────────────────────────────────── */
  const resumeDetail =
    showResumeModal && resumeFileUrl && resumeFile
      ? (() => (
          <div
            className="pg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="up-resume-title"
            onClick={() => setShowResumeModal(false)}
          >
            <div className="pg-modal-panel pg-modal-panel--wide" onClick={(e) => e.stopPropagation()}>
              <header className="pgm-head">
                <div className="pgm-head-main">
                  <h2 className="pgm-batch" id="up-resume-title">
                    {resumeFile.name}
                  </h2>
                  <dl className="pgm-ident">
                    <div className="pgm-ident-row">
                      <dt>Type</dt>
                      <dd>PDF</dd>
                    </div>
                    <div className="pgm-ident-row">
                      <dt>Size</dt>
                      <dd>{`${Math.max(1, Math.round(resumeFile.size / 1024))} KB`}</dd>
                    </div>
                  </dl>
                </div>

                <button
                  type="button"
                  className="pg-modal-close"
                  onClick={() => setShowResumeModal(false)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </header>

              <div className="pg-modal-body">
                <section className="pgm-section">
                  <h3 className="pgm-section-title">Preview</h3>
                  <iframe className="up-frame" src={resumeFileUrl} title="Resume preview" />
                </section>
              </div>

              <footer className="pg-modal-foot">
                <div className="pgm-actions">
                  <a
                    className="pgm-btn"
                    href={resumeFileUrl}
                    download={resumeFile.name || 'resume.pdf'}
                  >
                    Download
                  </a>
                </div>
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setShowResumeModal(false)}
                >
                  Close
                </button>
              </footer>
            </div>
          </div>
        ))()
      : null;

  /* ── The incomplete-profile popup ──────────────────────────────── */
  const incompleteDetail = showIncompleteModal ? (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="up-incomplete-title"
      onClick={() => setShowIncompleteModal(false)}
    >
      <div className="pg-modal-panel" onClick={(e) => e.stopPropagation()}>
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="up-incomplete-title">
              Complete your profile
            </h2>
            <p className="pgm-sub">
              These fields are still empty on your student record.
            </p>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={() => setShowIncompleteModal(false)}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          <section className="pgm-section">
            <h3 className="pgm-section-title">
              Missing
              <span className="pgm-section-count">{incompleteFields.length}</span>
            </h3>
            <ul className="up-missing">
              {incompleteFields.map((field, i) => (
                <li key={`${field}-${i}`}>
                  <X size={14} aria-hidden="true" />
                  {field}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <footer className="pg-modal-foot">
          <div className="pgm-actions">
            <button
              type="button"
              className="pgm-btn"
              onClick={() => {
                setActiveTab('Account Details');
                setEditMode(true);
                setShowIncompleteModal(false);
              }}
            >
              Fill them in
            </button>
          </div>
          <button
            type="button"
            className="pg-btn pg-btn--main"
            onClick={() => setShowIncompleteModal(false)}
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  ) : null;

  return (
    <div className="dashboard-wrapper">
      <div className="td-page pg">
        {/* ── Identity ──────────────────────────────────────────── */}
        <div className={`up-top${tiles.length === 0 ? ' up-top--solo' : ''}`}>
          <section className="bp-hero">
            <span className="bp-hero-eyebrow">{primaryRole.display} profile</span>
            <h1 className="bp-hero-title">{fullName || 'Your profile'}</h1>

            <div className="up-ident">
              <span className="up-avatar" aria-hidden="true">
                {initialsOf(fullName)}
              </span>
              <span className="up-ident-mail">{textOf(userData.email)}</span>
            </div>

            {/* The eyebrow already names the role that drives the tabs, so
                a single chip repeating it is noise. The chips earn their
                place only where an account holds more than one. */}
            {userRoles.length > 1 && (
              <div className="up-roles">
                {userRoles.map((role) => (
                  <span className="up-role" key={role.id || role.value}>
                    <RoleIcon size={12} aria-hidden="true" />
                    {role.label || role.value}
                  </span>
                ))}
              </div>
            )}

            {/* The dial is drawn only where a percentage exists. */}
            {isStudent() && hasAttendance && (
              <div className="bp-hero-figure">
                <span className="bp-hero-label">Attendance</span>
                <div
                  className="bp-gauge"
                  role="img"
                  aria-label={`Attendance ${attendancePercentage} percent`}
                >
                  <svg viewBox="0 0 200 116" aria-hidden="true">
                    <path className="bp-gauge-track" d={GAUGE_ARC} />
                    <path
                      className="bp-gauge-fill"
                      d={GAUGE_ARC}
                      strokeDasharray={GAUGE_LEN}
                      strokeDashoffset={GAUGE_LEN * (1 - attendancePercentage / 100)}
                    />
                  </svg>
                  <div className="bp-gauge-face">
                    <b>{attendancePercentage}</b>
                    <small>{attendanceStatus.text}</small>
                  </div>
                  <div className="bp-gauge-scale" aria-hidden="true">
                    <span>0</span>
                    <span>100</span>
                  </div>
                </div>
                <p className="bp-hero-note">
                  Classes attended against classes held, across every batch.
                </p>
              </div>
            )}

            {/* Counts of things the account actually has. The database id
                used to sit here and read as a 24-character hex string --
                nobody quotes that off a profile page -- and beside it a
                role count that was 0 whenever the roles failed to
                resolve. Both are gone; what is left is only shown when
                there is something to count. */}
            {isStudent() && (
              <dl className="bp-hero-facts">
                <div>
                  <dt>Batches</dt>
                  <dd>{batchesData.length}</dd>
                </div>
                <div>
                  <dt>Receipts</dt>
                  <dd>{receiptsData.length}</dd>
                </div>
                <div>
                  <dt>Classes attended</dt>
                  <dd>{textOf(attendanceStats?.presentCount)}</dd>
                </div>
              </dl>
            )}
          </section>

          {tiles.length > 0 && (
          <div className="ov-tiles">
            {tiles.map(({ icon: Icon, label, value, tone }) => (
              <div className="ov-tile" key={label}>
                <span className="ov-tile-label">
                  <Icon size={13} aria-hidden="true" />
                  {label}
                </span>
                <span className={`ov-tile-value${tone ? ` ${tone}` : ''}`}>{value}</span>
              </div>
            ))}
          </div>
          )}
        </div>

        {/* ── Tabs ──────────────────────────────────────────────── */}
        <div className="td-tabs">
          <div className="td-tabs-strip" role="tablist">
            {tabs.map((tab) => {
              const n = tabCount(tab);
              return (
                <button
                  type="button"
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={`td-tab${activeTab === tab ? ' is-on' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                  {n !== undefined && <span className="td-tab-n">{n}</span>}
                </button>
              );
            })}
          </div>

          <div className="td-tabs-body">
            {/* ── Account details ─────────────────────────────── */}
            {activeTab === 'Account Details' && !editMode && (
              <div className="up-panels">
                <div className="up-actions">
                  <button type="button" className="pg-btn pg-btn--main" onClick={() => setEditMode(true)}>
                    <Edit size={15} aria-hidden="true" />
                    Edit profile
                  </button>
                </div>

                <section className="ov-panel">
                  <header className="ov-panel-head">
                    <h2 className="ov-panel-title">Basic information</h2>
                  </header>
                  {renderFields(personalFields)}
                </section>

                {isStudent() && (
                  <section className="ov-panel">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">Emergency contact</h2>
                    </header>
                    {renderFields(emergencyFields)}
                  </section>
                )}

                {isInstructor() && (
                  <section className="ov-panel">
                    <header className="ov-panel-head">
                      <h2 className="ov-panel-title">Professional information</h2>
                    </header>
                    {renderFields(professionalFields)}
                  </section>
                )}

                {isStudent() && motivationalQuote && (
                  <p className="up-quote">{motivationalQuote}</p>
                )}
              </div>
            )}

            {activeTab === 'Account Details' && editMode && (
              <Formik
                initialValues={{
                  firstName: userData.firstName || '',
                  lastName: userData.lastName || '',
                  email: userData.email || '',
                  ...(isStudent() && {
                    phone: userData.phone || '',
                    city: userData.city || '',
                    state: userData.state || '',
                    documentNo: userData.documentNo || '',
                    collegeName: userData.collegeName || '',
                    courseName: userData.courseName || '',
                    permanentAddress: userData.permanentAddress || '',
                    currentAddress: userData.currentAddress || '',
                    emergencyContactName: userData.emergencyContactName || '',
                    emergencyContactRelationship: userData.emergencyContactRelationship || '',
                    emergencyContactPhone: userData.emergencyContactPhone || '',
                  }),
                  ...(isInstructor() && {
                    specialization: userData.specialization || '',
                    experience: userData.experience || '',
                    qualification: userData.qualification || '',
                  }),
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ isValid, dirty }) => (
                  <FormikForm className="up-panels">
                    <div className="up-actions">
                      <button
                        type="button"
                        className="pg-btn"
                        onClick={() => setEditMode(false)}
                      >
                        <X size={15} aria-hidden="true" />
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="pg-btn pg-btn--main"
                        disabled={!isValid || !dirty}
                      >
                        <Save size={15} aria-hidden="true" />
                        Save
                      </button>
                    </div>

                    <section className="ov-panel">
                      <header className="ov-panel-head">
                        <h2 className="ov-panel-title">Basic information</h2>
                      </header>
                      <div className="up-form">
                        {renderInput('First name', 'firstName')}
                        {renderInput('Last name', 'lastName')}
                        {renderInput('Email', 'email', 'email', true)}
                        {isStudent() && renderInput('Phone', 'phone', 'tel')}
                        {isStudent() && renderInput('City', 'city')}
                        {isStudent() && renderInput('State', 'state')}
                        {isStudent() && renderInput('Document number', 'documentNo')}
                        {isStudent() && renderInput('College', 'collegeName')}
                        {isStudent() && renderInput('Course', 'courseName')}
                        {isStudent() &&
                          renderInput('Permanent address', 'permanentAddress', 'text', true)}
                        {isStudent() &&
                          renderInput('Current address', 'currentAddress', 'text', true)}
                      </div>
                    </section>

                    {isStudent() && (
                      <section className="ov-panel">
                        <header className="ov-panel-head">
                          <h2 className="ov-panel-title">Emergency contact</h2>
                        </header>
                        <div className="up-form">
                          {renderInput('Contact name', 'emergencyContactName')}
                          {renderInput('Relationship', 'emergencyContactRelationship')}
                          {renderInput('Contact phone', 'emergencyContactPhone', 'tel')}
                        </div>
                      </section>
                    )}

                    {isInstructor() && (
                      <section className="ov-panel">
                        <header className="ov-panel-head">
                          <h2 className="ov-panel-title">Professional information</h2>
                        </header>
                        <div className="up-form">
                          {renderInput('Specialization', 'specialization')}
                          {renderInput('Experience', 'experience')}
                          {renderInput('Qualification', 'qualification', 'text', true)}
                        </div>
                      </section>
                    )}
                  </FormikForm>
                )}
              </Formik>
            )}

            {/* ── Batch information ───────────────────────────── */}
            {isStudent() && activeTab === 'Batch Information' && (
              <section
                className={`ov-panel${
                  batchesData.length > 0 ? ' ov-panel--flush' : ''
                }`}
              >
                <header className="ov-panel-head">
                  <h2 className="ov-panel-title">
                    Batches
                    <span className="ov-panel-count">{batchesData.length}</span>
                  </h2>
                </header>

                {!batchLoading && batchesData.length === 0 ? (
                  <div className="pg-state">
                    <span className="pg-state-icon">
                      <Users size={26} aria-hidden="true" />
                    </span>
                    <h3 className="pg-state-title">No batch information</h3>
                    <p className="pg-state-text">
                      You are not currently enrolled in any batch.
                    </p>
                  </div>
                ) : (
                  <div className="td-list td-list--upbatch">
                    <NewCustomDataTable
                      data={batchesData}
                      headers={batchHeaders}
                      loading={batchLoading}
                      showToolbar={false}
                      showPagination={false}
                      showFooter={false}
                      onRowClick={(row) => setSelectedBatch(row)}
                      /* Every cell wraps its text in a span, so the default
                         ['TR','TD'] would leave the text unclickable. */
                      validRowClickTagNames="*"
                      rowProps={(row) =>
                        rowKeyProps(`Open ${row.name || row.batchName || 'this batch'}`, () =>
                          setSelectedBatch(row)
                        )
                      }
                    />
                  </div>
                )}
              </section>
            )}

            {/* ── Billing ─────────────────────────────────────── */}
            {isStudent() && activeTab === 'Billing Information' && (
              <div className="up-panels">
                <div className="ov-tiles">
                  <div className="ov-tile">
                    <span className="ov-tile-label">
                      <CreditCard size={13} aria-hidden="true" />
                      Total fees
                    </span>
                    <span
                      className={`ov-tile-value${
                        moneyOf(userData.totalFees) === DASH ? ' up-none' : ''
                      }`}
                    >
                      {moneyOf(userData.totalFees)}
                    </span>
                  </div>
                  <div className="ov-tile">
                    <span className="ov-tile-label">
                      <CheckCircle size={13} aria-hidden="true" />
                      Paid
                    </span>
                    <span
                      className={`ov-tile-value${
                        moneyOf(userData.paidAmount) === DASH ? ' up-none' : ' up-paid'
                      }`}
                    >
                      {moneyOf(userData.paidAmount)}
                    </span>
                  </div>
                  <div className="ov-tile">
                    <span className="ov-tile-label">
                      <Award size={13} aria-hidden="true" />
                      Remaining
                    </span>
                    <span
                      className={`ov-tile-value${
                        moneyOf(userData.remainingFees) === DASH
                          ? ' up-none'
                          : Number(userData.remainingFees) > 0
                          ? ' up-due'
                          : ''
                      }`}
                    >
                      {moneyOf(userData.remainingFees)}
                    </span>
                  </div>
                </div>

                <section
                  className={`ov-panel${
                    receiptsData.length > 0 ? ' ov-panel--flush' : ''
                  }`}
                >
                  <header className="ov-panel-head">
                    <h2 className="ov-panel-title">
                      Receipts
                      <span className="ov-panel-count">{receiptsData.length}</span>
                    </h2>
                  </header>

                  {!receiptsLoading && receiptsData.length === 0 ? (
                    <div className="pg-state">
                      <span className="pg-state-icon">
                        <FileText size={26} aria-hidden="true" />
                      </span>
                      <h3 className="pg-state-title">No receipts found</h3>
                      <p className="pg-state-text">
                        No payment receipts are recorded against this account.
                      </p>
                    </div>
                  ) : (
                    <div className="td-list td-list--upfee">
                      <NewCustomDataTable
                        data={receiptsData}
                        headers={receiptHeaders}
                        loading={receiptsLoading}
                        showToolbar={false}
                        showPagination={false}
                        showFooter={false}
                        onRowClick={(row) => handleViewReceipt(row)}
                        validRowClickTagNames="*"
                        rowProps={(row) =>
                          rowKeyProps(
                            `Open receipt ${row.receiptNumber || row._id?.slice(-6) || ''}`,
                            () => handleViewReceipt(row)
                          )
                        }
                      />
                    </div>
                  )}
                </section>
              </div>
            )}

            {/* ── Resume ──────────────────────────────────────── */}
            {isStudent() && activeTab === 'Resume' && (
              <section className="ov-panel">
                <header className="ov-panel-head">
                  <h2 className="ov-panel-title">Resume</h2>
                </header>

                {resumeFile ? (
                  <div className="up-resume">
                    <span className="up-resume-mark">
                      <File size={18} aria-hidden="true" />
                    </span>
                    <div className="up-resume-main">
                      <div className="up-resume-name">{resumeFile.name}</div>
                      {/* Nothing is posted to the server yet, so the line
                          says where the file actually is. */}
                      <p className="up-resume-sub">
                        Selected on this device — not yet saved to your record.
                      </p>
                    </div>
                    <div className="up-resume-actions">
                      <button
                        type="button"
                        className="pg-btn"
                        onClick={() => setShowResumeModal(true)}
                      >
                        <Eye size={15} aria-hidden="true" />
                        View
                      </button>
                      <a
                        className="pg-btn"
                        href={resumeFileUrl}
                        download={resumeFile.name || 'resume.pdf'}
                      >
                        <Download size={15} aria-hidden="true" />
                        Download
                      </a>
                      <button
                        type="button"
                        className="pg-btn"
                        onClick={() => {
                          setResumeFile(null);
                          setResumeFileUrl(null);
                        }}
                      >
                        <X size={15} aria-hidden="true" />
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="up-drop">
                    <span className="up-drop-mark">
                      <File size={26} aria-hidden="true" />
                    </span>
                    <h3 className="up-drop-title">No resume selected</h3>
                    <p className="up-drop-text">
                      Choose a PDF to preview it here. It stays on this device until the
                      upload endpoint is wired up.
                    </p>
                    <label className="pg-btn pg-btn--main" htmlFor="up-resume-upload">
                      <Upload size={15} aria-hidden="true" />
                      Choose PDF
                      <input
                        className="up-drop-input"
                        id="up-resume-upload"
                        type="file"
                        accept="application/pdf"
                        onChange={handleResumeUpload}
                      />
                    </label>
                  </div>
                )}
              </section>
            )}

            {/* ── Placeholders ────────────────────────────────
                These tabs have no data source behind them yet. They say
                so rather than filling the panel with invented rows. */}
            {isInstructor() && activeTab === 'Teaching Information' && (
              <div className="pg-state">
                <span className="pg-state-icon">
                  <BookOpen size={26} aria-hidden="true" />
                </span>
                <h3 className="pg-state-title">Teaching information</h3>
                <p className="pg-state-text">
                  Your courses and teaching statistics are not published to this page yet.
                </p>
              </div>
            )}

            {isInstructor() && activeTab === 'Schedule' && (
              <div className="pg-state">
                <span className="pg-state-icon">
                  <Calendar size={26} aria-hidden="true" />
                </span>
                <h3 className="pg-state-title">Schedule</h3>
                <p className="pg-state-text">
                  Your class schedule is not published to this page yet.
                </p>
              </div>
            )}

            {isAdmin() && activeTab === 'System Settings' && (
              <div className="pg-state">
                <span className="pg-state-icon">
                  <Settings size={26} aria-hidden="true" />
                </span>
                <h3 className="pg-state-title">System settings</h3>
                <p className="pg-state-text">
                  System configuration is not published to this page yet.
                </p>
              </div>
            )}

            {isAdmin() && activeTab === 'Reports' && (
              <div className="pg-state">
                <span className="pg-state-icon">
                  <FileText size={26} aria-hidden="true" />
                </span>
                <h3 className="pg-state-title">Reports</h3>
                <p className="pg-state-text">
                  Reports and analytics are not published to this page yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {batchDetail}
      {receiptDetail}
      {resumeDetail}
      {incompleteDetail}
    </div>
  );
};

export default UserProfile;
